import { NgModule } from '@angular/core';
import type { Routes } from '@angular/router';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { AuthGuard } from './shared/guards/auth.guard';
import { LiteClinicGuard } from './shared/guards/lite-clinic.guard';
import { SccGuard } from './scc/shared/guards/scc.guard';
import { isScc } from './shared/services/app-state.service';

const routesMyPacs: Routes = [
  { path: '', redirectTo: '/studies', pathMatch: 'full' },
  {
    path: 'login',
    loadChildren: async () => (await import('./components/login/login.module')).LoginModule,
  },
  {
    path: 'not-found',
    loadChildren: async () => (await import('./components/not-found/not-found.module')).NotFoundModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'forgot-password',
    loadChildren: async () => (await import('./components/forgot-password/forgot-password.module')).ForgotPasswordModule,
    canActivate: [AuthGuard]
  },
  // Connected routes
  {
    path: 'album-cases/:id',
    loadChildren: async () => (await import('./components/album-cases/album-cases.module')).AlbumCasesModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'albums',
    loadChildren: async () => (await import('./components/albums/albums.module')).AlbumsModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'dashboard',
    loadChildren: async () => (await import('./components/dashboard/dashboard.module')).DashboardModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'billing',
    loadChildren: async () => (await import('./components/billing/billing.module')).BillingModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'dicomizer',
    loadChildren: async () => (await import('./components/dicomizer/dicomizer.module')).DicomizerModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'dicomizer/:studyId',
    loadChildren: async () => (await import('./components/dicomizer/dicomizer.module')).DicomizerModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'disciplined',
    loadChildren: async () => (await import('./components/disciplined/disciplined.module')).DisciplinedModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'edit-report',
    loadChildren: async () => (await import('./components/edit-report/edit-report.module')).EditReportModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'keywords',
    loadChildren: async () => (await import('./components/keywords/keywords.module')).KeywordsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'radio-protection',
    loadChildren: async () => (await import('./components/radio-protection/radio-protection.module')).RadioProtectionModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'refering-vet',
    loadChildren: async () => (await import('./components/refering-vet/refering-vets.module')).ReferingVetsModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'studies',
    loadChildren: async () => (await import('./components/studies/studies.module')).StudiesModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'study-detail/:studyId',
    loadChildren: async () => (await import('./components/study-detail/study-detail.module')).StudyDetailModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'worklist',
    loadChildren: async () => (await import('./components/worklist/worklist.module')).WorklistModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'viewer',
    canActivate: [AuthGuard],
    loadChildren: async () => (await import('./components/viewer/viewer.module')).ViewerModule
  },
  {
    path: 'lite/clinic/:clinicId/study/:studyUid',
    loadChildren: async () => (await import('./components/lite-redirect/lite-redirect.module')).LiteRedirectModule
  },
  // Admin
  {
    path: 'clinics',
    loadChildren: async () => (await import('./components/clinics/clinics.module')).ClinicsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'clinic-detail/:clinicId',
    loadChildren: async () => (await import('./components/clinic-details/clinic-details.module')).ClinicDetailsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'users',
    loadChildren: async () => (await import('./components/users/users.module')).UsersModule,
    canActivate: [AuthGuard, LiteClinicGuard]
  },
  {
    path: 'report-templates',
    loadChildren: async () => (await import('./components/report-templates/report-templates.module')).ReportTemplatesModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'report-header',
    loadChildren: async () => (await import('./components/report-header/report-header.module')).ReportHeaderModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'my-account',
    loadChildren: async () => (await import('./components/my-account/my-account.module')).MyAccountModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'mail-configuration',
    loadChildren: async () => (await import('./components/mail-configuration/mail-configuration.module')).MailConfigurationModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'reports',
    loadChildren: async () => (await import('./components/reports/reports.module')).ReportsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'important-alerts',
    loadChildren: async () => (await import('./components/important-alerts/important-alerts.module')).ImportantAlertsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'scc',
    loadChildren: async () => (await import('./scc/scc.module')).SccModule,
    canActivate: [SccGuard]
  },
  {
    path: 'admin-dashboard',
    loadComponent: async () => import('./components/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'pms-integrations',
    loadChildren: async () => (await import('./components/pms-integrations/pms-integrations.module')).PmsIntegrationsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'tutorials',
    loadChildren: async () => (await import('./components/tutorials/tutorials.module')).TutorialsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'zendesk-sso',
    loadChildren: async () => (await import('./components/zendesk-sso/zendesk-sso.module')).ZendeskSsoModule,
    canActivate: [AuthGuard]
  },
  { path: '**', redirectTo: '/not-found', pathMatch: 'full' },
];

const routesScc: Routes = [
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  {
    path: 'login',
    loadChildren: async () => (await import('./scc/components/scc-login/scc-login.module')).SccLoginModule,
  },
  {
    path: 'register',
    loadChildren: async () => (await import('./scc/components/scc-register/scc-register.module')).SccRegisterModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'confirm-clinic',
    loadChildren: async () => (await import('./scc/components/scc-confirm-clinic/scc-confirm-clinic.module')).SccConfirmClinicModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'not-found',
    loadChildren: async () => (await import('./components/not-found/not-found.module')).NotFoundModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'forgot-password',
    loadChildren: async () => (await import('./components/forgot-password/forgot-password.module')).ForgotPasswordModule,
    canActivate: [AuthGuard]
  },
  // Connected routes
  {
    path: 'upload',
    loadChildren: async () => (await import('./scc/components/scc-upload/scc-upload.module')).SccUploadModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'viewer',
    canActivate: [AuthGuard],
    loadChildren: async () => (await import('./components/viewer/viewer.module')).ViewerModule
  },
  {
    path: 'edit-report',
    loadChildren: async () => (await import('./scc/components/scc-report/scc-report.module')).SccReportModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'help',
    loadChildren: async () => (await import('./scc/components/scc-help/scc-help.module')).SccHelpModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'studies',
    pathMatch: 'full',
    redirectTo: 'dashboard'
  },
  // Admin
  {
    path: 'my-account',
    loadChildren: async () => (await import('./components/my-account/my-account.module')).MyAccountModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'users',
    loadChildren: async () => (await import('./components/users/users.module')).UsersModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'clinics',
    loadChildren: async () => (await import('./components/clinics/clinics.module')).ClinicsModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'clinic-payment-method',
    loadChildren: async () => (await import('./scc/components/scc-payment-method-page/scc-payment-method-page.module')).SccPaymentMethodPageModule,
    canActivate: [AuthGuard]
  },
  {
    path: 'dashboard',
    loadChildren: async () => (await import('./scc/components/scc-dashboard/scc-dashboard.module')).SccDashboardModule,
    canActivate: [AuthGuard]
  },
  { path: '**', redirectTo: '/not-found', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(isScc() ? routesScc : routesMyPacs, {
    preloadingStrategy: PreloadAllModules,
    enableTracing: false,
    initialNavigation: 'enabledBlocking',
    anchorScrolling: 'enabled',
    scrollPositionRestoration: 'enabled'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
